// UserCompanyBranchAccess only. Shared table methods are in tableMixin.js.
// Methods here with the same name as a tableMixin method replace it on this page.
export default {
    name: "UserCompanyBranchAccess",
    methods: {
        // Add button / Alt+N: empty window, no user picked, both lists empty
        clearForm() {
            this.isEditing = false;
            this.isSaving = false;
            this.submitted = false;
            this.defaultAlert.pageOrModal = "";

            this.selectedAccessUserId = "";
            this.formInputs = this.getDefaultFormInputs();

            this.availableCompanies = [];
            this.assignedCompanies = [];
            this.availableSelectedBranches = {};
            this.assignedSelectedBranches = {};
            this.expandedAvailableCompanies = [];
            this.expandedAssignedCompanies = [];
        },
        // This page shows only the first server message (message[0])
        handleError(daOrigin, error, daClass, daPageOrModal) {
            this.button_text = "";
            this.alertType = "danger";
            if (error && error.data && error.data.message) {
                this.defaultAlert.alertMessage = error.data.message[0];
            } else {
                this.defaultAlert.alertMessage = error;
            }
            this.showAlert = true;
            this.handleAlert(daOrigin, this.defaultAlert.alertMessage, daClass, daPageOrModal);
            console.error("Error:", this.defaultAlert.alertMessage);
        },
        handleFetchRolesSuccess(response) {
            this.rolesOptions = response.data.results;
        },

        /* ---------------- Picker: open / close companies ---------------- */
        // Only one company open at a time
        toggleAvailableCompanyExpanded(companyId) {
            const index = this.expandedAvailableCompanies.indexOf(companyId);
            this.expandedAvailableCompanies = index === -1 ? [companyId] : [];
        },
        toggleAssignedCompanyExpanded(companyId) {
            const index = this.expandedAssignedCompanies.indexOf(companyId);
            this.expandedAssignedCompanies = index === -1 ? [companyId] : [];
        },
        isAvailableCompanyExpanded(companyId) {
            return this.expandedAvailableCompanies.includes(companyId);
        },
        isAssignedCompanyExpanded(companyId) {
            return this.expandedAssignedCompanies.includes(companyId);
        },

        /* ---------------- Picker: tick a whole company ---------------- */
        toggleAvailableCompany(company) {
            const branchIds = company.branches.map((branch) => Number(branch.id));
            const allSelected = branchIds.length > 0 && branchIds.every((id) => this.availableSelectedBranches[id]);

            branchIds.forEach((id) => {
                if (allSelected) {
                    delete this.availableSelectedBranches[id];
                } else {
                    this.availableSelectedBranches[id] = true;
                }
            });
        },
        // The protected Head Office is never ticked on the Assigned side
        toggleAssignedCompany(company) {
            const branchIds = company.branches
                .filter((branch) => !this.isProtectedHeadOffice(company, branch))
                .map((branch) => Number(branch.id));
            const allSelected = branchIds.length > 0 && branchIds.every((id) => this.assignedSelectedBranches[id]);

            branchIds.forEach((id) => {
                if (allSelected) {
                    delete this.assignedSelectedBranches[id];
                } else {
                    this.assignedSelectedBranches[id] = true;
                }
            });
        },
        isAvailableCompanyFullySelected(company) {
            if (!company.branches || company.branches.length === 0) {
                return false;
            }
            return company.branches.every((branch) => !!this.availableSelectedBranches[Number(branch.id)]);
        },
        isAvailableCompanyPartiallySelected(company) {
            if (!company.branches || company.branches.length === 0) {
                return false;
            }
            const selectedCount = company.branches.filter((branch) => !!this.availableSelectedBranches[Number(branch.id)]).length;
            return selectedCount > 0 && selectedCount < company.branches.length;
        },
        // The locked Head Office can't be ticked, so it doesn't count
        isAssignedCompanyFullySelected(company) {
            const branches = (company.branches || []).filter((branch) => !this.isProtectedHeadOffice(company, branch));
            if (branches.length === 0) {
                return false;
            }
            return branches.every((branch) => !!this.assignedSelectedBranches[Number(branch.id)]);
        },
        isAssignedCompanyPartiallySelected(company) {
            const branches = (company.branches || []).filter((branch) => !this.isProtectedHeadOffice(company, branch));
            if (branches.length === 0) {
                return false;
            }
            const selectedCount = branches.filter((branch) => !!this.assignedSelectedBranches[Number(branch.id)]).length;
            return selectedCount > 0 && selectedCount < branches.length;
        },

        /* ---------------- Picker: tick one branch ---------------- */
        toggleAvailableBranch(company, branch) {
            const id = Number(branch.id);
            if (this.availableSelectedBranches[id]) {
                delete this.availableSelectedBranches[id];
            } else {
                this.availableSelectedBranches[id] = true;
            }
        },
        toggleAssignedBranch(company, branch) {
            if (this.isProtectedHeadOffice(company, branch)) {
                return;
            }
            const id = Number(branch.id);
            if (this.assignedSelectedBranches[id]) {
                delete this.assignedSelectedBranches[id];
            } else {
                this.assignedSelectedBranches[id] = true;
            }
        },
        isAvailableBranchSelected(companyId, branchId) {
            return !!this.availableSelectedBranches[Number(branchId)];
        },
        isAssignedBranchSelected(companyId, branchId) {
            return !!this.assignedSelectedBranches[Number(branchId)];
        },

        /* ---------------- Picker: moving between the lists ---------------- */
        moveSelectedToAssigned() {
            const selectedBranchIds = Object.keys(this.availableSelectedBranches)
                .filter((id) => this.availableSelectedBranches[id])
                .map(Number);

            if (selectedBranchIds.length === 0) {
                return;
            }

            let receivingCompanyId = null;

            this.availableCompanies.forEach((company) => {
                const selectedBranches = company.branches.filter((branch) =>
                    selectedBranchIds.includes(Number(branch.id))
                );

                if (selectedBranches.length === 0) {
                    return;
                }

                let assignedCompany = this.assignedCompanies.find((item) => Number(item.id) === Number(company.id));

                if (!assignedCompany) {
                    assignedCompany = {
                        id: company.id,
                        company_name: company.company_name,
                        branches: [],
                    };
                    this.assignedCompanies.push(assignedCompany);
                }

                selectedBranches.forEach((branch) => {
                    const exists = assignedCompany.branches.some((item) => Number(item.id) === Number(branch.id));
                    if (!exists) {
                        assignedCompany.branches.push(branch);
                    }
                });

                // Remember the company that received the branch(es)
                receivingCompanyId = Number(company.id);

                company.branches = company.branches.filter((branch) =>
                    !selectedBranchIds.includes(Number(branch.id))
                );
            });

            // Remove empty companies from Available
            this.availableCompanies = this.availableCompanies.filter((company) => company.branches.length > 0);

            // Clear selected branches
            selectedBranchIds.forEach((id) => {
                delete this.availableSelectedBranches[id];
            });

            this.assignedSelectedBranches = {};

            // Collapse all Assigned companies, then open only the one that received the branches
            this.expandedAssignedCompanies = [];
            if (receivingCompanyId !== null) {
                this.expandedAssignedCompanies.push(receivingCompanyId);
            }
        },
        moveSelectedToAvailable() {
            const selectedBranchIds = Object.keys(this.assignedSelectedBranches)
                .filter((id) => this.assignedSelectedBranches[id])
                .map(Number);

            if (selectedBranchIds.length === 0) {
                return;
            }

            let receivingCompanyId = null;

            this.assignedCompanies.forEach((company) => {
                const selectedBranches = company.branches.filter((branch) =>
                    selectedBranchIds.includes(Number(branch.id))
                );

                if (selectedBranches.length === 0) {
                    return;
                }

                let availableCompany = this.availableCompanies.find((item) => Number(item.id) === Number(company.id));

                if (!availableCompany) {
                    availableCompany = {
                        id: company.id,
                        company_name: company.company_name,
                        branches: [],
                    };
                    this.availableCompanies.push(availableCompany);
                }

                selectedBranches.forEach((branch) => {
                    const exists = availableCompany.branches.some((item) => Number(item.id) === Number(branch.id));
                    if (!exists) {
                        availableCompany.branches.push(branch);
                    }
                });

                // Remember the company that received the branch(es)
                receivingCompanyId = Number(company.id);

                company.branches = company.branches.filter((branch) =>
                    !selectedBranchIds.includes(Number(branch.id))
                );
            });

            // Remove empty companies from Assigned
            this.assignedCompanies = this.assignedCompanies.filter((company) => company.branches.length > 0);

            // Clear selected branches
            selectedBranchIds.forEach((id) => {
                delete this.assignedSelectedBranches[id];
            });

            this.availableSelectedBranches = {};

            // Collapse all Available companies, then open only the one that received the branches
            this.expandedAvailableCompanies = [];
            if (receivingCompanyId !== null) {
                this.expandedAvailableCompanies.push(receivingCompanyId);
            }
        },
    },
};